import React, { useState } from 'react';
import { Upload, Download } from 'lucide-react';
import { openingApi } from '../api/opening';
import { useToast } from '../hooks/useToast';

const OpeningBalances: React.FC = () => {
  const { showToast } = useToast();
  const [tab, setTab] = useState('stock');
  const [rows, setRows] = useState<any[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const tabs = [
    { key:'stock', label:'Opening Stock' },
    { key:'ar', label:'Opening AR' },
    { key:'ap', label:'Opening AP' },
    { key:'coa', label:'COA Balances' },
  ];

  const templates: Record<string,string> = {
    stock: 'sku,product_name,category_name,unit_symbol,branch_name,location_name,qty,unit_cost,selling_price,as_of_date\n25KG,BARIIS DAIMOND,RASHIN,KIISH,Galkacyo,Sales Glk B1,1000,12.5,15,2026-06-30',
    ar: 'mobile,customer_name,opening_balance,as_of_date,branch_name,note\n612345001,Ahmed Farah,500,2026-06-01,Garowe Branch,Customer owes us',
    ap: 'phone,supplier_name,opening_balance,as_of_date,branch_name,note\n971500000001,Microphone STOR Dubai,2165,2026-06-01,Garowe Branch,We owe supplier',
    coa: 'account_code,account_name,account_type,balance,as_of_date,branch_name\n1002,Dahabshiil,asset,702855,2026-07-01,Garowe Branch',
  };

  const downloadTemplate = () => {
    const blob = new Blob([templates[tab]], { type:'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = tab + '_opening_template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    setLoading(true); setRows([]); setErrors([]);
    try {
      const fns: any = { stock: openingApi.previewStock, ar: openingApi.previewAR, ap: openingApi.previewAP, coa: openingApi.previewCOA };
      const res = await fns[tab](file);
      setRows(res.data.data.rows || []);
      setErrors(res.data.data.errors || []);
      showToast(res.data.data.total + ' rows ready', res.data.data.errors?.length > 0 ? 'warning' : 'success');
    } catch (err: any) { showToast(err.response?.data?.message || 'Upload failed', 'error'); }
    finally { setLoading(false); }
  };

  const handleImport = async () => {
    if (!rows.length) { showToast('No rows to import', 'error'); return; }
    setSaving(true);
    try {
      const fns: any = { stock: () => openingApi.importStock({ rows }), ar: () => openingApi.importAR({ rows }), ap: () => openingApi.importAP({ rows }), coa: () => openingApi.importCOA({ rows }) };
      const res = await fns[tab]();
      showToast(res.data.message);
      setRows([]); setErrors([]);
    } catch (err: any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  return (
    <>
      <div className='page-header'>
        <h1 className='page-title'>Opening Balances</h1>
        <div style={{ display:'flex', gap:8 }}>
          <button className='btn btn-secondary' onClick={downloadTemplate}><Download size={13}/> Template</button>
          <label className='btn btn-primary' style={{ cursor:'pointer' }}>
            <Upload size={13}/> {loading ? 'Loading...' : 'Upload CSV'}
            <input type='file' accept='.csv,.txt' style={{ display:'none' }} onChange={handleFile} disabled={loading} />
          </label>
        </div>
      </div>
      <div style={{ display:'flex', gap:4, marginBottom:16, borderBottom:'2px solid var(--bdr)' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => { setTab(t.key); setRows([]); setErrors([]); }} style={{ padding:'8px 16px', fontSize:12, fontWeight:600, border:'none', background:'none', cursor:'pointer', borderBottom: tab===t.key ? '2px solid var(--blue)' : '2px solid transparent', color: tab===t.key ? 'var(--blue)' : 'var(--tx3)', marginBottom:'-2px' }}>{t.label}</button>
        ))}
      </div>
      {errors.length > 0 && <div className='alert alert-warning' style={{ marginBottom:12 }}><strong>{errors.length} warnings:</strong><ul style={{ margin:'8px 0 0 16px', fontSize:11 }}>{errors.map((e,i) => <li key={i}>{e}</li>)}</ul></div>}
      {rows.length > 0 && <>
        <div style={{ marginBottom:8, display:'flex', justifyContent:'space-between' }}>
          <span style={{ fontSize:12, fontWeight:600 }}>Preview — {rows.length} rows</span>
          <button className='btn btn-primary' onClick={handleImport} disabled={saving}>{saving ? 'Importing...' : 'Import ' + rows.length + ' Records'}</button>
        </div>
        <div className='table-wrap'><table>
          <thead><tr>
            {tab==='stock' && <><th>SKU</th><th>Product</th><th>Branch</th><th>Location</th><th>Qty</th><th>Cost</th><th>Price</th><th>Status</th></>}
            {tab==='ar' && <><th>Customer</th><th>Mobile</th><th>Balance</th><th>Date</th><th>Branch</th><th>Status</th></>}
            {tab==='ap' && <><th>Supplier</th><th>Phone</th><th>Balance</th><th>Date</th><th>Branch</th><th>Status</th></>}
            {tab==='coa' && <><th>Code</th><th>Account</th><th>Type</th><th>Balance</th><th>Date</th><th>Status</th></>}
          </tr></thead>
          <tbody>
            {tab==='stock' && rows.map((r,i) => <tr key={i}><td className='td-code'>{r.sku}</td><td>{r.product_name}</td><td style={{color:r.branch_id?'var(--tx2)':'var(--red)'}}>{r.branch_name}</td><td style={{color:r.location_id?'var(--tx2)':'var(--red)'}}>{r.location_name}</td><td className='td-num'>{r.qty}</td><td className='td-num'>{Number(r.unit_cost).toFixed(2)}</td><td className='td-num td-green'>{Number(r.selling_price).toFixed(2)}</td><td>{r.is_new_product?<span className='badge badge-amber'>New</span>:<span className='badge badge-green'>Exists</span>}</td></tr>)}
            {tab==='ar' && rows.map((r,i) => <tr key={i}><td style={{fontWeight:600}}>{r.name}</td><td style={{color:'var(--tx3)'}}>{r.mobile||'—'}</td><td className='td-num' style={{color:r.balance>=0?'var(--blue)':'var(--red)',fontWeight:700}}>{Number(r.balance).toFixed(2)}</td><td style={{color:'var(--tx3)',fontSize:11}}>{r.as_of_date}</td><td style={{color:r.branch_id?'var(--tx2)':'var(--red)'}}>{r.branch_name}</td><td>{r.is_new?<span className='badge badge-amber'>New</span>:<span className='badge badge-green'>Exists</span>}</td></tr>)}
            {tab==='ap' && rows.map((r,i) => <tr key={i}><td style={{fontWeight:600}}>{r.name}</td><td style={{color:'var(--tx3)'}}>{r.phone||'—'}</td><td className='td-num' style={{color:r.balance>=0?'var(--red)':'var(--green)',fontWeight:700}}>{Number(r.balance).toFixed(2)}</td><td style={{color:'var(--tx3)',fontSize:11}}>{r.as_of_date}</td><td style={{color:r.branch_id?'var(--tx2)':'var(--red)'}}>{r.branch_name}</td><td>{r.is_new?<span className='badge badge-amber'>New</span>:<span className='badge badge-green'>Exists</span>}</td></tr>)}
            {tab==='coa' && rows.map((r,i) => <tr key={i}><td className='td-code'>{r.account_code}</td><td style={{fontWeight:600}}>{r.account_name}</td><td><span className='badge badge-blue'>{r.account_type}</span></td><td className='td-num' style={{fontWeight:700}}>{Number(r.balance).toFixed(2)}</td><td style={{color:'var(--tx3)',fontSize:11}}>{r.as_of_date}</td><td>{r.is_new?<span className='badge badge-amber'>New</span>:<span className='badge badge-green'>Exists</span>}</td></tr>)}
          </tbody>
        </table></div>
      </>
      }
      {rows.length === 0 && !loading && <div className='empty-state'><p>Upload a CSV file to preview data before importing.</p></div>}
    </>
  );
};

export default OpeningBalances;